import React from "react";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  MenuItem,
  Select,
  Typography,
  type SelectChangeEvent,
} from "@mui/material";
import type {
  ComparisonDimension,
  UseComparisonModeResult,
} from "../hooks/useComparisonMode";

interface ComparisonToggleProps {
  comparison: UseComparisonModeResult;
}

/**
 * Average | Compare toggle, with an inline "Compare by" picker shown when more
 * than one filter dimension is eligible (2+ values selected). Disabled with an
 * explanatory tooltip when nothing is eligible.
 */
export const ComparisonToggle: React.FC<ComparisonToggleProps> = ({
  comparison,
}) => {
  const { enabled, dimension, eligible, canCompare, setEnabled, setDimension, label } =
    comparison;

  const handleMode = (_: React.MouseEvent<HTMLElement>, value: string | null) => {
    if (value === null) return;
    setEnabled(value === "compare");
  };

  const handleDimension = (event: SelectChangeEvent) => {
    setDimension(event.target.value as ComparisonDimension);
  };

  const toggle = (
    <ToggleButtonGroup
      value={enabled ? "compare" : "average"}
      exclusive
      onChange={handleMode}
      size="small"
      disabled={!canCompare}
    >
      <ToggleButton value="average">Average</ToggleButton>
      <ToggleButton value="compare">Compare</ToggleButton>
    </ToggleButtonGroup>
  );

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.5 }}>
      {canCompare ? (
        toggle
      ) : (
        <Tooltip
          title="Select 2 or more regions, departments, age ranges or shift patterns in the filter to compare them side by side."
          arrow
        >
          {/* span wrapper so the tooltip works on a disabled control */}
          <span>{toggle}</span>
        </Tooltip>
      )}

      {enabled && eligible.length > 1 && dimension && (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
          <Typography variant="body2" color="text.secondary">
            Compare by
          </Typography>
          <Select
            size="small"
            value={dimension}
            onChange={handleDimension}
            sx={{ minWidth: 140 }}
          >
            {eligible.map((d) => (
              <MenuItem key={d} value={d}>
                {label(d)}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}
    </Box>
  );
};
